import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../i18n';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatDate } from '../file-browser/utils';
import { useFlightLinksQuery } from './flightLinks.queries';
import { useLinkFlightMutation, useUnlinkFlightMutation } from './flightLinks.mutations';
import { logDay, organizationList, splitCandidates } from './flightLinkModel';
import { startEvidence } from './flightLinkCopy';
import type { FlightLinkCandidate, FlightLinkFlight } from '../../types/api';

interface LinkBinToFlightDialogProps {
  /** The `.bin` log's registry id. Undefined closes the dialog. */
  objectId?: string;
  /** The log's name, for the title. */
  label?: string;
  onClose: () => void;
}

/**
 * Link an ArduPilot `.bin` log to its flight record (#1694).
 *
 * Why it exists: a flight share conveys the flight's own CSV, and since #1694
 * every file linked to the flight as well. Nothing in a `.bin` names its drone
 * or flight, so the link is written here, by an admin who knows, never inferred.
 *
 * What it says before the click, because linking is a share decision: which
 * organizations the flight is shared with, i.e. who receives this log the
 * moment it is linked. Proposals (same day, start time near the flight's) are
 * marked, not applied.
 */
export const LinkBinToFlightDialog: React.FC<LinkBinToFlightDialogProps> = ({
  objectId,
  label,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const [day, setDay] = useState<string | undefined>(undefined);
  const query = useFlightLinksQuery(objectId, day);
  const linkMutation = useLinkFlightMutation();
  const unlinkMutation = useUnlinkFlightMutation();
  const [actionError, setActionError] = useState<string | null>(null);

  const close = () => {
    setDay(undefined);
    setActionError(null);
    onClose();
  };

  const busy = linkMutation.isPending || unlinkMutation.isPending;
  const data = query.data;
  const { proposed, others } = splitCandidates(data?.candidates ?? []);

  const handleLink = async (flight: FlightLinkCandidate) => {
    if (!objectId) return;
    setActionError(null);
    try {
      const result = await linkMutation.mutateAsync({ objectId, flightId: flight.flight_id });
      showSuccess(t(result.created === false ? 'flightLinks.alreadyLinked' : 'flightLinks.linked'));
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  };

  const handleUnlink = async (flight: FlightLinkFlight) => {
    if (!objectId) return;
    if (!window.confirm(t('flightLinks.unlinkConfirm'))) return;
    setActionError(null);
    try {
      await unlinkMutation.mutateAsync({ objectId, flightId: flight.flight_id });
      showSuccess(t('flightLinks.unlinked'));
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  };

  const flightTitle = (flight: FlightLinkFlight) =>
    flight.log_basename ||
    t('flightLinks.flightOn', {
      serial: flight.drone_serial ?? '?',
      date: flight.flight_date ? formatDate(flight.flight_date) : '?',
    });

  const reach = (flight: FlightLinkFlight, linked: boolean) =>
    flight.shared_with.length === 0
      ? t('flightLinks.notShared')
      : t(linked ? 'flightLinks.sharedWithLinked' : 'flightLinks.sharedWith', {
          orgs: organizationList(flight.shared_with, t('flightLinks.unknownOrg')),
        });

  const evidence = (c: FlightLinkCandidate) => startEvidence(t, c);

  const candidateItem = (c: FlightLinkCandidate) => (
    <ListItem
      key={c.flight_id}
      divider
      data-testid="flight-link-candidate"
      secondaryAction={
        <Button
          size="small"
          variant={c.proposed ? 'contained' : 'outlined'}
          disabled={busy}
          onClick={() => handleLink(c)}
          data-testid="flight-link-link"
        >
          {t('flightLinks.link')}
        </Button>
      }
    >
      <ListItemText
        primary={
          <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {flightTitle(c)}
            {c.proposed && <Chip size="small" color="primary" label={t('flightLinks.proposed')} />}
          </Box>
        }
        secondary={[evidence(c), reach(c, false)].filter(Boolean).join(' — ')}
        sx={{ pr: 10 }}
      />
    </ListItem>
  );

  return (
    <Dialog open={!!objectId} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{t('flightLinks.dialogTitle', { name: label ?? '' })}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('flightLinks.intro')}
        </Typography>

        {query.isLoading && <LinearProgress />}
        {query.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getErrorMessage(query.error)}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {data && (
          <>
            <Typography variant="subtitle2">{t('flightLinks.current')}</Typography>
            {data.links.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('flightLinks.noLinks')}
              </Typography>
            ) : (
              <List dense sx={{ mb: 1 }}>
                {data.links.map((link) => (
                  <ListItem
                    key={link.flight_id}
                    divider
                    data-testid="flight-link-current"
                    secondaryAction={
                      // Offered only where the delete policy will honour it: the
                      // owning org, or the org that wrote the link.
                      link.removable ? (
                        <Button
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => handleUnlink(link)}
                        >
                          {t('flightLinks.unlink')}
                        </Button>
                      ) : undefined
                    }
                  >
                    <ListItemText
                      primary={flightTitle(link)}
                      secondary={reach(link, true)}
                      sx={{ pr: 10 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {t('flightLinks.candidates')}
              </Typography>
              <TextField
                type="date"
                size="small"
                label={t('flightLinks.day')}
                value={day ?? logDay(data.log_start)}
                onChange={(e) => setDay(e.target.value || undefined)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'flight-link-day' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {data.log_start
                ? t('flightLinks.logStart', { time: data.log_start.replace('T', ' ') })
                : t('flightLinks.noLogStart')}
            </Typography>
            {data.candidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('flightLinks.noCandidates')}
              </Typography>
            ) : (
              <List dense>
                {proposed.map(candidateItem)}
                {others.map(candidateItem)}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
